<h1 class="sectionHeader">Connecting Dash to a Google Workspace service account</h1>

<p class="intro">
  Connecting Dash to Google Workspace through a service account is recommended for most organizations. The setup
  procedure must be performed by a global administrator, and it requires access to the Google Developer Console. If this
  is not possible, then you can connect Dash to a <a routerLink="/guide/google-personal-account">standard or personal account</a> instead.
</p>


<ol class="instructionList">
  <li>
    <div>
      <p><b>Create a new project:</b><br/> Visit the
        <a href="https://console.developers.google.com/" target="_blank">Google developer console</a>.  You'll need to be signed in with
        an admin account for your Google Workspace organization.<br/><br/>  Click the project selector at the top of the
        page.  It will either say "Select a project" (if you haven't previously created a project), or will show the
        name of your most recently used project.  Click <i>New Project</i>.<br><br>
        Give the project a name (such as "Dash meeting room display") and make sure that the <i>Organization</i> and
        <i>Location</i> fields are both set to your organization.  Click <i>Create</i>.
      </p>
      <app-illustration alt="Create a new project in the Google Developer Console" class="illustration googleCreateNewProject" src="../../assets/images/google-create-project.png" webpSrc="../../assets/images/google-create-project.webp" backgroundPosition="left" width="170" height="224" cover="true"></app-illustration>
    </div>
  </li>
  <li>
    <div>
      <p><b>Enable APIs:</b><br/> Visit these two pages and click the <i>Enable</i> button to turn on the Admin and Calendar API for your project:<br><br>
        <a href="https://console.cloud.google.com/apis/api/admin.googleapis.com/" target="_blank">Google Admin API</a><br><br>
        <a href="https://console.developers.google.com/apis/api/calendar-json.googleapis.com" target="_blank">Google Calendar API</a><br><br>
        Before clicking <i>Enable</i>, make sure that the correct project is selected at the top of the page.
      </p>
      <app-illustration alt="Enable the Google APIs that Dash needs" class="illustration googleActivateApi" src="../../assets/images/google-activate-api.png" webpSrc="../../assets/images/google-activate-api.webp" backgroundPosition="left" width="170" height="224" cover="true"></app-illustration>
    </div>
  </li>
  <li>
    <div>
      <div class="liContainer">
        <p><b>Create a service account:</b><br/> Make sure that your new project is selected at the top of the page,
          then click on <i>Credentials</i> in the menu on the left of the page.  Click <i>Create credentials</i> near
          the top of the page, then click "Service account"
          </p>

        <p>Enter a name for the service account (e.g. "Dash Service account") and click <i>Create and continue</i>.
          Skip the <i>Grant this service account access to the project</i> section by pressing <i>Continue</i>, then
          click <i>Done</i>.</p>

      </div>

      <app-illustration alt="Create a Google Service Account for Dash" class="illustration googleCreateServiceAccount" src="../../assets/images/google-create-service-account.png" webpSrc="../../assets/images/google-create-service-account.webp" backgroundPosition="left" width="170" height="224" cover="true"></app-illustration>
    </div>
  </li>
  <li>
    <div>
      <div class="liContainer">
        <p><b>Grant access in your Google Workspace account:</b><br/>
          Click on the newly created service account, then expand the <i>Advanced settings</i> section.  Under
          <i>Domain wide delegation</i>, copy the service account's client ID.<br><br>

          In a new tab, open the domain wide delegation section of the
          <a href="https://admin.google.com/ac/owl/domainwidedelegation" target="_blank">Google workspace admin console</a>.  Click
          <i>Add new</i> and paste the client ID you just copied.  Enter the following OAuth scopes:<br><br>
        </p>
        <p class="tip permissions-container">
          <span class="permissions">{{permissions}}</span>
          <span *ngIf="!hasCopied" class="copy-icon"><a (click)="copyCredentials($event)" href="#">Copy</a></span>
          <span *ngIf="hasCopied" class="copy-icon">Copied!</span>
        </p>
        <p>If you would like to display the color of our meetings in the Dash display, you will also need to add the
        following scope:</p>
        <p class="tip permissions-container">
          <span class="permissions">{{permissions2}}</span>
          <span *ngIf="!hasCopied2" class="copy-icon"><a (click)="copyCredentials2($event)" href="#">Copy</a></span>
          <span *ngIf="hasCopied2" class="copy-icon">Copied!</span>
        </p>
        <p>Click <i>Authorize</i> when you're done to save the permissions.</p>
        <p>You can find more information about how these permissions are used at the bottom of this page.</p>

      </div>
      <app-illustration alt="Set up Domain-wide Delegation for your Dash service account and grant the necessary permissions" class="illustration googleGrantDomainWideDelegation" src="../../assets/images/google-domain-wide-delegation.png" webpSrc="../../assets/images/google-domain-wide-delegation.webp" backgroundPosition="left" width="170" height="214" cover="true"></app-illustration>
    </div>

  </li>
  <li>
    <div>
      <p><b>Create a private key:</b><br/>Return to the previous tab (the service account overview in the Google
        developer console), and open the <i>Keys</i> tab.  Click <i>Add key</i> and choose <i>Create new key</i>.<br><br>
        Choose "JSON" as the key type, and click "Create".  Google will now download the key file to your computer.
        Note where this key file was saved.
      </p>
      <app-illustration alt="Create a private key for your service account" class="illustration googleCreatePrivateKey" src="../../assets/images/google-create-key.png" webpSrc="../../assets/images/google-create-key.webp" backgroundPosition="left" width="170" height="170" cover="true"></app-illustration>
    </div>
  </li>
</ol>


<h2>Connecting Dash to the service account</h2>
<p>
  Once you have completed the steps above, you can connect Dash to your Google Workspace organization.  Open
  Dash and go to Settings (the <i>cog</i> icon in the bottom right of the main display).  If this is your first time using Dash,
  you will be asked to choose a calendar service immediately.  If you have used Dash before you should see the name
  of the calendar service you are currently connected to, and a <i>Change</i> button.  Click <i>Change</i>.
</p>
<p>
  You should see a list of calendar services to choose from (Microsoft 365, Google, and your device calendar).  Select
  <i>Google Workspace or Google Account</i> and then select <i>Google Workspace Service Account</i>.  On the next screen, enter the
  following credentials into the app.  It can be helpful to send the credentials electronically to the device
  first, and then copy+paste them into the fields.
</p>
<ul>
  <li><b>Client email: </b>The <i>client_email</i> field from the file you downloaded in step 5.  Make sure not to
    include the quotes surrounding the value.</li>
  <li><b>Admin user email address: </b>The email address of a global administrator user account in your Google Workspace
    organization.  Dash will query your user directory and perform actions on your resource calendars as this user, so
    the account needs to have permissions to access these resources.</li>
  <li><b>Private key:</b> The <i>private_key</i> field from the file you downloaded in step 5.  Make sure not to include
    the quotes surrounding the value.</li>
</ul>
<p>
  Once you have entered the credentials, click <i>Next</i> and Dash will connect to your Google Workspace organization and
  give you a list of your organization's meeting rooms.  For more information on getting Dash set up, please see our
  <a routerLink="/guide/quickStart">Quick start guide</a>.
</p>

<p>&nbsp;</p>
<p>&nbsp;</p>

<p class="tip icon"><b>Dash requires several permissions to function.  Below is a list of each permission, and the
  reason each is required:</b><br><br>
  <span class="code">https://www.googleapis.com/auth/admin.directory.resource.calendar</span> - Dash uses this permission to detect
  and query your resource calendars.<br><br>
  <span class="code">https://www.googleapis.com/auth/calendar.events</span> - Dash uses this permission to read events
  in your resource calendars, and to create or modify events when you make bookings from the Dash display<br><br>
  <span class="code">https://www.googleapis.com/auth/admin.directory.domain.readonly, and
    https://www.googleapis.com/auth/admin.directory.user.readonly</span> - Dash uses these permissions to search your
  Google Workspace user directory when adding attendees or setting the organizer for meetings booked from the display<br><br>
  <span class="code">https://www.googleapis.com/auth/calendar.readonly</span> - OPTIONAL If you book meetings in Google
  Calendar and set a color, Dash can show the color on the display.  This permission is required so that Dash can look
  up the color in the organizer's calendar.<br><br>
  Dash devices communicate directly with Google Workspace, so none of your sensitive information is shared with us or
  our servers.  The credential information is safely stored in your device's secure storage.
  <br>
</p>
